import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal, Steps, WindowLoader } from "medere-ui-framework";
import { Button } from "primereact/button";
import { Toast, ToastMessage } from "primereact/toast";
import PrescriptionStepStudyOrder from "./Steps/PrescriptionStepStudyOrder";
import ConfirmationStepStudyOrder from "./Steps/ConfirmationStepStudyOrder";
import PatientStepStudyOrder from "./Steps/PatientStepStudyOrder";
import CreatePatientForStudyOrderWrapper from "./Steps/CreatePatient/CreatePatientStudyOrderWrapper";
import "./NewStudyOrderView.scss";
import { EStudyOrderSteps, EStudyOrderViews } from "../StudyOrderEnums";
import { getPatientData } from "../../../services/digitalRPservice";
import {
  createStudyOrder,
  getStudyOrderDiseaseByExactCie10,
  getStudyOrderCopyPayload,
  getStudyOrderErrorMessage,
  generatePdf,
  getStudyOrderPDF,
  getStudyOrderValidationRules,
  prevalidateStudyOrder,
  StudyOrderValidationRulesDTO,
} from "../../../services/StudyOrderController";
import { getTerminologyCie10FromSnomed } from "../../../services/TerminologyController";
import { getFirstInvalidItemMessage } from "./studyOrderValidation";

import {HealthPatientCoverage, Professional, StudyOrderCopyPayloadDTO, StudyOrderData, StudyOrderItem} from "../../../interface/interface";
import StudyOrderPrescriptionModal from "../StudyOrderPrescriptionModal";
import type { ResolvedStudyOrderContext } from "../StudyOrderHistoryView";
import type { StudyOrderFlowParams } from "../StudyOrderFlowParams";

interface NewStudyOrderViewProps {
  professional?: Professional;
  goToSearchProfessional?: () => void;
  onOpenOrderFlow?: (params?: StudyOrderFlowParams) => void;
  setActiveView: (v: number | undefined) => void;
  setSubActiveView: (v: number | undefined) => void;
  setPrevActiveView: (v: number | undefined) => void;
  setPrevSubActiveView: (v: number | undefined) => void;
  onExitFlow?: () => void;
  initialStep?: EStudyOrderSteps;
  initialSelectedPatient?: any;
}

const toLocalDateString = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeCopiedItems = (items: any[] | undefined | null): StudyOrderItem[] => {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((it, idx) => {
    const code = it?.code ?? undefined;
    const description = it?.description ?? "";
    const nomenclatorId = it?.nomenclatorId ?? null;
    const nomenclatorSourceId = it?.nomenclatorSourceId ?? null;
    const observations = it?.observations ?? null;

    const origin =
      nomenclatorId ? "NOMENCLATOR" :
      nomenclatorSourceId ? "NOMENCLATOR_SOURCE" :
      code || description ? "FREE_TEXT" : "FREE_TEXT";

    return {
      id: it?.id ?? `COPY-${nomenclatorId ?? "X"}-${code ?? "NC"}-${idx}`,
      code,
      description,
      observations,
      nomenclatorId,
      nomenclatorSourceId,
      origin,
    } as any;
  });
};

const normalizeText = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const extractRepositoryHints = (coverage: any): string[] => {
  if (!coverage) return [];
  return [
    coverage?.repository,
    coverage?.repositoryDesc,
    coverage?.repositoryDescription,
    coverage?.repositorytypedescription,
    coverage?.repositorytypeDescription,
    coverage?.healthentityplan?.repository,
    coverage?.healthentityplan?.repositoryDesc,
    coverage?.healthentityplan?.healthentity?.repository,
    coverage?.healthentityplan?.healthentity?.repositoryDesc,
  ]
    .filter(Boolean)
    .map((value: any) => String(value));
};

const buildFlowQuery = (params?: StudyOrderFlowParams) => {
  const qs = new URLSearchParams();

  if (params?.patientId) qs.set("medereentityID", String(params.patientId));
  if (params?.professionalId) qs.set("professionalId", String(params.professionalId));
  if (params?.healthPatientServiceId) qs.set("healthPatientServiceId", String(params.healthPatientServiceId));
  if (params?.studyType) qs.set("studyType", params.studyType);
  if (params?.mode) qs.set("mode", params.mode);
  if (params?.copyFromHpsId) qs.set("copyFromHpsId", String(params.copyFromHpsId));
  if (params?.scopeToPatientOnly) qs.set("scopeToPatient", "true");

  return qs;
};

export default function NewStudyOrderView({
  professional,
  goToSearchProfessional,
  onOpenOrderFlow,
  setActiveView,
  setSubActiveView,
  setPrevActiveView,
  setPrevSubActiveView,
  onExitFlow,
  initialStep,
  initialSelectedPatient,
}: NewStudyOrderViewProps) {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCoverage, setSelectedCoverage] = useState<HealthPatientCoverage | undefined>(undefined);
  const medereEntityID = Number(searchParams.get("medereentityID") ?? 0);

  const professionalIdFromUrl = searchParams.get("professionalId")
    ? Number(searchParams.get("professionalId"))
    : null;

  const hpsIdFromUrl = searchParams.get("healthPatientServiceId")
    ? Number(searchParams.get("healthPatientServiceId"))
    : null;

  const studyTypeFromUrl = searchParams.get("studyType") ?? undefined;

  const scopeToPatientFromUrl = searchParams.get("scopeToPatient") === "true";

  const copyFromHpsId = searchParams.get("copyFromHpsId")
    ? Number(searchParams.get("copyFromHpsId"))
    : null;

  const isCopyFlow =
    searchParams.get("mode") === "COPY_STUDY_ORDER" && !!copyFromHpsId;

  const initialStepFromUrl =
    searchParams.get("initialStep") === "CONFIRMATION"
      ? EStudyOrderSteps.CONFIRMATIONSTEP
      : undefined;

  const resolvedProfessionalId =
    professional?.professionaid ??
    (professionalIdFromUrl ? professionalIdFromUrl : null);

  const [activeStep, setActiveStep] = useState<EStudyOrderSteps>(() => {
    if (isCopyFlow) return EStudyOrderSteps.PRESCRIPTIONSTEP;
    return initialStepFromUrl ?? initialStep ?? EStudyOrderSteps.PATIENTSTEP;
  });

  const [isStepValid, setIsStepValid] = useState<boolean>(() => {
    if (isCopyFlow) return false;
    return !!initialSelectedPatient;
  });

  const [selectedPatient, setSelectedPatient] = useState<any>(
    initialSelectedPatient ?? null
  );

  const [studyOrderData, setStudyOrderData] = useState<StudyOrderData>(() => ({
    emissionDate: new Date(),
    items: [],
  } as any));
  const [validationRules, setValidationRules] = useState<StudyOrderValidationRulesDTO | null>(null);

  const [activeStudyOrderView, setActiveStudyOrderView] =
    useState<EStudyOrderViews>(EStudyOrderViews.NEWSTUDYORDER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prevalidationFailed, setPrevalidationFailed] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>("");
  const failedValidationPayloadRef = useRef<any>(null);
  const [createdOrderContext, setCreatedOrderContext] = useState<ResolvedStudyOrderContext | null>(null);
  const [createdOrderLoading, setCreatedOrderLoading] = useState(false);
  const handleCreatedOrderPatientUpdated = useCallback((patientUpdated: any) => {
    setSelectedPatient(patientUpdated);
    setCreatedOrderContext((prev) =>
      prev ? { ...prev, patient: patientUpdated } : prev
    );
  }, []);

  const legacyActiviaFlow = useMemo(() => {
    const repositoryFromQuery = searchParams.get("repository");
    const selectedInsurance =
      selectedCoverage ??
      ((selectedPatient as any)?.insurances?.find((i: any) => i?.primarycoverage) ??
        (selectedPatient as any)?.insurances?.[0] ??
        null);

    const repositoryHints = [
      repositoryFromQuery,
      ...extractRepositoryHints(selectedInsurance),
    ]
      .filter(Boolean)
      .map(normalizeText);

    if (repositoryHints.some((value) => value === "activia")) {
      return true;
    }

    // Fallback defensivo para datos viejos que todavia no envian "repository".
    const legacyCoverageTexts = [
      selectedInsurance?.description,
      selectedInsurance?.healthentityplan?.name,
      selectedInsurance?.healthentityplan?.healthentity?.commercialname,
    ]
      .filter(Boolean)
      .map(normalizeText);

    return legacyCoverageTexts.some((value) => value.includes("activia"));
  }, [selectedCoverage, selectedPatient, searchParams]);

  const isActiviaFlow = useMemo(() => {
    const validatorId = normalizeText(validationRules?.validatorIdentifier);
    if (validatorId) {
      return validatorId === "activia";
    }
    return legacyActiviaFlow;
  }, [validationRules, legacyActiviaFlow]);

  const showMessage = useCallback(
    (ref: React.RefObject<Toast>, msg: any, severity: ToastMessage["severity"]) => {
      ref.current?.show({ severity, summary: msg, life: 3000 });
    },
    []
  );

  const showLongMessage = useCallback(
    (ref: React.RefObject<Toast>, msg: any, severity: ToastMessage["severity"]) => {
      const content = <div style={{ fontWeight: 600 }}>{msg}</div>;
      ref.current?.show({ severity, summary: msg, life: 3000, content });
    },
    []
  );

  const handlePrescriptionError = useCallback(
    (error: any, message: string) => {
      console.error("Error buscando diagnostico:", error);
      if ((message ?? "").length > 90) {
        showLongMessage(toast, message, "error");
        return;
      }
      showMessage(toast, message, "error");
    },
    [showLongMessage, showMessage]
  );

  const openCreatedOrderModal = useCallback(
    async (studyOrderId: number) => {
      setCreatedOrderLoading(true);
      setCreatedOrderContext(null);
      setActiveStudyOrderView(EStudyOrderViews.SENDSTUDYORDERVIEW);

      try {
        await generatePdf(studyOrderId);
        const pdfFile = await getStudyOrderPDF(studyOrderId);

        setCreatedOrderContext({
          orderId: studyOrderId,
          patientId: selectedPatient?.medereentity ?? undefined,
          healthPatientServiceId: hpsIdFromUrl ?? undefined,
          professionalId: resolvedProfessionalId ?? undefined,
          patient: selectedPatient ?? null,
          pdfEncoded: pdfFile?.encodedPdf,
          pdfFileId: pdfFile?.id,
        });
      } catch (error) {
        handlePrescriptionError(error, "No se pudo preparar el PDF de la orden creada.");
      } finally {
        setCreatedOrderLoading(false);
      }
    },
    [hpsIdFromUrl, handlePrescriptionError, resolvedProfessionalId, selectedPatient]
  );

  const exitToHistory = () => {
    const qs = new URLSearchParams();
    if (medereEntityID) qs.set("medereentityID", String(medereEntityID));
    if (professionalIdFromUrl)
      qs.set("professionalId", String(professionalIdFromUrl));
    if (hpsIdFromUrl) qs.set("healthPatientServiceId", String(hpsIdFromUrl));
    if (studyTypeFromUrl) qs.set("studyType", String(studyTypeFromUrl));
    if (scopeToPatientFromUrl) qs.set("scopeToPatient", "true");
    startTransition(() => {
      navigate(`/studyorderhistoryview?${qs.toString()}`);
    });
  };

  useEffect(() => {
    if (!resolvedProfessionalId && goToSearchProfessional) {
      goToSearchProfessional();
    }
  }, [resolvedProfessionalId, goToSearchProfessional]);

  useEffect(() => {
    const patientId = Number((selectedPatient as any)?.medereentity ?? 0);
    const healthPatientCoverageId = Number((selectedCoverage as any)?.healthpatientcoverage ?? 0);
    if (!patientId && !healthPatientCoverageId) {
      setValidationRules(null);
      return;
    }

    getStudyOrderValidationRules({
      patientId: patientId || undefined,
      healthPatientCoverageId: healthPatientCoverageId || undefined,
    })
      .then((rules) => setValidationRules(rules ?? null))
      .catch((err) => {
        console.error("Error obteniendo reglas de validacion de orden", err);
        setValidationRules(null);
      });
  }, [selectedPatient, selectedCoverage]);

  useEffect(() => {
    if (isCopyFlow) return;
    if (!medereEntityID) return;

    getPatientData(medereEntityID)
      .then((patient) => {
        if (!patient) return;
        setSelectedPatient(patient);
        const fav = patient.insurances?.find((i:any)=>i.primarycoverage) ?? patient.insurances?.[0];
        setSelectedCoverage(fav);
        setIsStepValid(true);
      })
      .catch((err) => console.error("Error cargando paciente", err));
  }, [medereEntityID, isCopyFlow]);

  useEffect(() => {
    if (!isCopyFlow || !copyFromHpsId) return;

    setIsStepValid(false);

    getStudyOrderCopyPayload(copyFromHpsId)
      .then(async (payload: StudyOrderCopyPayloadDTO) => {
      const payloadPatientIdRaw = payload?.patientId;
      const urlPatientIdRaw = medereEntityID;

      const payloadPatientId = Number(payloadPatientIdRaw ?? 0);
      const urlPatientId = Number(urlPatientIdRaw ?? 0);

      const patientIdToLoad =
        payloadPatientId > 0 ? payloadPatientId :
        urlPatientId > 0 ? urlPatientId :
        null;

      if (patientIdToLoad) {
        try {
          const patient = await getPatientData(patientIdToLoad);
          setSelectedPatient(patient ?? null);
          const fav = patient?.insurances?.find((i:any)=>i.primarycoverage) ?? patient?.insurances?.[0];
          setSelectedCoverage(fav);
        } catch (e) {
          console.error("Error cargando paciente copyFlow", e);
          setSelectedPatient(null);
        }
      } else {
        // si ambos vienen vacíos, no hay forma de cargar paciente desde el front
        setSelectedPatient(null);
      }

      const emissionDateAsDate = payload?.emissionDate
        ? new Date(`${payload.emissionDate}T00:00:00`)
        : new Date();

      const normalizedItems = normalizeCopiedItems(payload?.items as any);

      setStudyOrderData((prev) => ({
        ...prev,
        diagnosisType: payload?.diagnosisType ?? (prev as any).diagnosisType ?? "FREE_TEXT",
        diagnosis: payload?.diagnosis ?? "",
        diagnosisSnomedCode: payload?.diagnosisSnomedCode ?? null,
        diagnosisSnomedDisplay: payload?.diagnosisSnomedDisplay ?? null,
        diagnosisIcd10Code: payload?.diagnosisIcd10Code ?? null,
        diagnosisIcd10Display: payload?.diagnosisIcd10Display ?? null,
        sampleDescription: payload?.sampleDescription ?? "",
        description: payload?.description ?? "",
        externalId: payload?.externalId ?? null,
        mailTo: payload?.mailTo ?? null,
        studyTypeId: payload?.studyTypeId ?? null,
        studyType: (prev as any).studyType ?? null,
        items: normalizedItems,
        emissionDate: emissionDateAsDate,
      } as any));

      setActiveStep(EStudyOrderSteps.PRESCRIPTIONSTEP);
    })
      .catch((err) => {
        console.error("Error precargando copia", err);
        setActiveStep(EStudyOrderSteps.PRESCRIPTIONSTEP);
        setIsStepValid(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCopyFlow, copyFromHpsId, medereEntityID]);

  const steps = useMemo(
    () => [{ label: "Paciente" }, { label: "Prescripción" }, { label: "Confirmación" }],
    []
  );

  const isPatientStep = activeStep === EStudyOrderSteps.PATIENTSTEP;

  const handleNext = async () => {
    if (isSubmitting) return;
    if (!isStepValid) return;

    if (activeStep < EStudyOrderSteps.CONFIRMATIONSTEP) {
      setActiveStep(activeStep + 1);
      return;
    }

    try {
      const items = Array.isArray((studyOrderData as any).items)
        ? (studyOrderData as any).items
        : [];
      const allowedSourceIds = (validationRules?.allowedNomenclatorSourceIds ?? [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const invalidMessage = getFirstInvalidItemMessage({
        items,
        diagnosisType: (studyOrderData as any).diagnosisType,
        diagnosis: (studyOrderData as any).diagnosis,
        diagnosisIcd10Code: (studyOrderData as any).diagnosisIcd10Code,
        requiresMappedCie10: Boolean(validationRules?.diagnosisMappedToCie10Required),
        allowedSourceIds: isActiviaFlow ? (allowedSourceIds.length ? allowedSourceIds : [1]) : allowedSourceIds,
      });
      if (invalidMessage) {
        handlePrescriptionError(new Error(invalidMessage), invalidMessage);
        return;
      }

      const emission = studyOrderData.emissionDate;

      const studyDate = emission instanceof Date ? `${toLocalDateString(emission)}T00:00:00` : null;

      const payload = {
        patientId: selectedPatient?.medereentity ?? null,
        hpsId: hpsIdFromUrl ?? null,
        healthPatientCoverageId: selectedCoverage?.healthpatientcoverage ?? null,

        professionalId: resolvedProfessionalId ?? null,
        studyTypeId: (studyOrderData as any).studyType?.studyType ?? (studyOrderData as any).studyTypeId ?? null,

        diagnosisType: (studyOrderData as any).diagnosisType ?? "FREE_TEXT",
        clinicData: (studyOrderData as any).clinicData ?? null,
        sampleDescription: (studyOrderData as any).sampleDescription ?? null,
        presumptiveDiagnostic: (studyOrderData as any).diagnosis ?? null,
        diagnosisSnomedCode: (studyOrderData as any).diagnosisSnomedCode ?? null,
        diagnosisSnomedDisplay: (studyOrderData as any).diagnosisSnomedDisplay ?? null,
        diagnosisIcd10Code: (studyOrderData as any).diagnosisIcd10Code ?? null,
        diagnosisIcd10Display: (studyOrderData as any).diagnosisIcd10Display ?? null,
        description: (studyOrderData as any).description ?? null,
        externalId: (studyOrderData as any).externalId ?? null,
        mailTo: (studyOrderData as any).mailTo ?? null,
        hidePatientData: !!(studyOrderData as any).hidePatientData,
        studyDate,

        items: (items ?? []).map((it: any) => ({
          description: it.description ?? null,
          code: it.code ?? null,
          observations: it.observations ?? null,
          nomenclatorId: it.nomenclatorId ?? null,
          nomenclatorSourceId: it.nomenclatorSourceId ?? null,
          origin: it.origin ?? null,
        })),
      };

      const diagnosisType = String(payload.diagnosisType ?? "").toUpperCase();
      const needsMappedCie10 = Boolean(validationRules?.diagnosisMappedToCie10Required);
      if (diagnosisType === "SNOMED" && needsMappedCie10 && !payload.diagnosisIcd10Code) {
        const snomedCode = String(payload.diagnosisSnomedCode ?? "").trim();
        if (snomedCode) {
          try {
            const mappings = await getTerminologyCie10FromSnomed(snomedCode);
            const first = mappings?.[0];
            const mappedCode =
              String(first?.cie10code ?? "").trim() || null;
            const mappedDisplay =
              String(first?.cie10Description ?? "").trim() ||
              String(first?.cie10title ?? "").trim() ||
              null;
            let finalDisplay = mappedDisplay;
            if (!finalDisplay && mappedCode) {
              try {
                const disease = await getStudyOrderDiseaseByExactCie10(mappedCode);
                finalDisplay = String(disease?.name ?? "").trim() || null;
              } catch {
                finalDisplay = null;
              }
            }

            if (mappedCode) {
              payload.diagnosisIcd10Code = mappedCode;
              payload.diagnosisIcd10Display = finalDisplay;
              setStudyOrderData((prev) => ({
                ...(prev as any),
                diagnosisIcd10Code: mappedCode,
                diagnosisIcd10Display: finalDisplay,
              }));
            }
          } catch {
            // El prevalidate/backend devolvera el error final si sigue sin mapeo.
          }
        }
      }

      setIsSubmitting(true);
      const prevalidation = await prevalidateStudyOrder(payload);
      if (prevalidation?.rules) {
        setValidationRules(prevalidation.rules);
      }
      if (!prevalidation?.valid) {
        const formattedErrors = Array.isArray(prevalidation?.errors)
          ? prevalidation.errors.filter((entry) => typeof entry === "string" && entry.trim().length > 0)
          : [];
        const message =
          formattedErrors.length > 0
            ? formattedErrors.join("\n")
            : "La orden no cumple las reglas de validacion del repositorio.";
        failedValidationPayloadRef.current = payload;
        setValidationErrorMessage(message);
        setPrevalidationFailed(true);
        return;
      }

      setPrevalidationFailed(false);
      failedValidationPayloadRef.current = null;

      let studyOrderId: any;
      try {
        studyOrderId = await createStudyOrder(payload);
      } catch (err) {
        const message = getStudyOrderErrorMessage(err, "La orden no pudo ser emitida.");
        failedValidationPayloadRef.current = payload;
        setValidationErrorMessage(message);
        setPrevalidationFailed(true);
        return;
      }

      setStudyOrderData((prev) => ({
        ...prev,
        studyOrderId,
      } as any));

      void openCreatedOrderModal(Number(studyOrderId));
    } catch (err) {
      const message = getStudyOrderErrorMessage(
        err,
        "Se produjo un error inesperado al crear la orden."
      );
      handlePrescriptionError(err, message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceEmit = async () => {
    if (isSubmitting) return;
    const payload = failedValidationPayloadRef.current;
    if (!payload) return;

    try {
      setIsSubmitting(true);
      const forcedPayload = { ...payload, forceStudyOrder: true };
      const studyOrderId = await createStudyOrder(forcedPayload);

      failedValidationPayloadRef.current = null;
      setStudyOrderData((prev) => ({ ...prev, studyOrderId } as any));
      void openCreatedOrderModal(Number(studyOrderId));
    } catch (err) {
      const message = getStudyOrderErrorMessage(err, "Se produjo un error inesperado al crear la orden.");
      setValidationErrorMessage(message);
      setPrevalidationFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setPrevalidationFailed(false);
    setValidationErrorMessage("");
    failedValidationPayloadRef.current = null;

    if (isCopyFlow && activeStep === EStudyOrderSteps.PRESCRIPTIONSTEP) {
      if (onExitFlow) {
        onExitFlow();
        return;
      }
      exitToHistory();
      return;
    }

    if (activeStep > EStudyOrderSteps.PATIENTSTEP) {
      setActiveStep(activeStep - 1);
      return;
    }

    if (onExitFlow) onExitFlow();
  };

  const resetFlow = () => {
    setSelectedPatient(null);
    setSelectedCoverage(undefined);
    setStudyOrderData({ emissionDate: new Date(), items: [] } as any);
    setActiveStep(EStudyOrderSteps.PATIENTSTEP);
    setIsStepValid(false);
    setActiveStudyOrderView(EStudyOrderViews.NEWSTUDYORDER);
    setCreatedOrderContext(null);
    setCreatedOrderLoading(false);
    setPrevalidationFailed(false);
    setValidationErrorMessage("");
    failedValidationPayloadRef.current = null;
    if (onExitFlow) onExitFlow();
  };

  const goToCreatePatient = () => setActiveStudyOrderView(EStudyOrderViews.CREATEPATIENTVIEW);
  const returnToStudyOrder = () => setActiveStudyOrderView(EStudyOrderViews.NEWSTUDYORDER);

  const renderStepContent = () => {
    switch (activeStep) {
      case EStudyOrderSteps.PATIENTSTEP:
        return (
          <PatientStepStudyOrder
            professional={professional}
            selectedPatient={selectedPatient}
            setSelectedPatient={setSelectedPatient}
            selectedCoverage={selectedCoverage}
            setSelectedCoverage={setSelectedCoverage}
            onValidationChange={setIsStepValid}
            setActiveView={setActiveView}
            setSubActiveView={setSubActiveView}
            setPrevActiveView={setPrevActiveView}
            setPrevSubActiveView={setPrevSubActiveView}
            goToCreatePatient={goToCreatePatient}
            onExitFlow={resetFlow}
            handleNext={handleNext}
            handleBack={handleBack}
            isStepValid={isStepValid}
          />
        );

      case EStudyOrderSteps.PRESCRIPTIONSTEP:
        return (
          <PrescriptionStepStudyOrder
            studyOrderData={studyOrderData as any}
            setStudyOrderData={setStudyOrderData as any}
            onValidationChange={setIsStepValid}
            selectedPatient={selectedPatient}
            healthPatientCoverageId={selectedCoverage?.healthpatientcoverage ?? null}
            onError={handlePrescriptionError}
            isActiviaFlow={isActiviaFlow}
            validationRules={validationRules}
          />
        );

      case EStudyOrderSteps.CONFIRMATIONSTEP:
        return (
          <ConfirmationStepStudyOrder
            patient={selectedPatient}
            selectedCoverage={selectedCoverage}
            studyOrderData={studyOrderData}
            onValidationChange={setIsStepValid}
          />
        );

      default:
        return null;
    }
  };

  if (activeStudyOrderView === EStudyOrderViews.CREATEPATIENTVIEW) {
    return (
      <CreatePatientForStudyOrderWrapper
        selectedPatient={selectedPatient}
        setSelectedPatient={setSelectedPatient}
        returnToStudyOrder={returnToStudyOrder}
      />
    );
  }

  if (activeStudyOrderView === EStudyOrderViews.SENDSTUDYORDERVIEW) {
    return (
      <StudyOrderPrescriptionModal
        visible
        loading={createdOrderLoading}
        context={createdOrderContext}
        expired={false}
        onClose={resetFlow}
        onRepeat={() => {
          if (hpsIdFromUrl) {
            setCreatedOrderContext(null);
            setCreatedOrderLoading(false);
            setActiveStudyOrderView(EStudyOrderViews.NEWSTUDYORDER);
            const params: StudyOrderFlowParams = {
              patientId: selectedPatient?.medereentity ?? (medereEntityID > 0 ? medereEntityID : undefined),
              healthPatientServiceId: hpsIdFromUrl,
              professionalId: resolvedProfessionalId ?? undefined,
              studyType: studyTypeFromUrl,
              mode: "COPY_STUDY_ORDER",
              copyFromHpsId: hpsIdFromUrl,
              scopeToPatientOnly: scopeToPatientFromUrl || undefined,
            };

            if (onOpenOrderFlow) {
              onOpenOrderFlow(params);
              return;
            }

            startTransition(() => {
              const qs = buildFlowQuery(params);
              navigate(`/studyorderview?${qs.toString()}`);
            });
          } else {
            resetFlow();
          }
        }}
        onPatientUpdated={handleCreatedOrderPatientUpdated}
        hideCancelConfirmation={false}
        allowPrimaryEditOnMissingContact
      />
    );
  }

  return (
    <div className="new-study-order-view">
      <Toast ref={toast} position="top-right" />
      <h1 className="ns-title">Nueva orden</h1>

      <Steps
        steps={steps}
        activeStep={activeStep}
        stepWidth="100%"
        labelStyle={{ color: "#1C324F" }}
        style={{ marginBottom: "1.2rem" }}
      />

      <div className="ns-content">{renderStepContent()}</div>

      {!isPatientStep && (
        <div className="ns-footer">
          <Button
            label={
              activeStep === EStudyOrderSteps.CONFIRMATIONSTEP ? "Finalizar" : "Continuar"
            }
            onClick={handleNext}
            disabled={!isStepValid || isSubmitting}
            className="ns-btn-primary"
          />
          <Button
            label="Volver"
            onClick={handleBack}
            disabled={isSubmitting}
            className="ns-btn-secondary"
          />
        </div>
      )}
      <WindowLoader showLoader={isSubmitting} className="whiteSpinner" />

      <Modal
        visible={prevalidationFailed}
        setVisible={(v: boolean) => {
          if (!v) {
            setPrevalidationFailed(false);
            setValidationErrorMessage("");
            failedValidationPayloadRef.current = null;
          }
        }}
        header={
          <div style={{ display: "flex", flexDirection: "column", textAlign: "center", paddingLeft: "10%" }}>
            <i className="pi pi-exclamation-circle" style={{ fontSize: "48px", color: "#A53E2B" }} />
            <label style={{ marginTop: "10px" }}>
              <strong>La obra social no pudo validar el pedido de estudio</strong>
            </label>
          </div>
        }
        width="w-medium"
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          {validationErrorMessage && (
            <label style={{ fontSize: "14px", marginBottom: "16px" }}>
              <strong>Motivo: </strong>{validationErrorMessage}
            </label>
          )}
          <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
            <Button
              label="Revisar el pedido"
              onClick={() => {
                setPrevalidationFailed(false);
                setValidationErrorMessage("");
                failedValidationPayloadRef.current = null;
                setActiveStep(EStudyOrderSteps.PRESCRIPTIONSTEP);
              }}
              style={{ marginBottom: "15px", backgroundColor: "#14C7BC", border: "none" }}
            />
            <Button
              label="Emitir igualmente sin validar"
              onClick={() => {
                setPrevalidationFailed(false);
                handleForceEmit();
              }}
              style={{ marginBottom: "15px", backgroundColor: "#FFFFFF", color: "#545454", border: "1px solid #14C7BC" }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
